import { useAppSelector } from 'app/hooks';
import AlertBox from 'blocks/AlertBox';
import Button from 'blocks/Button';
import BaseModal from 'components/modals/BaseModal';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserTierState } from 'store/tiers/tiersSlice';
import ajax from 'utils/ajax';
import urls from 'utils/urls';
import { ContentProps, ModalTypes } from './teransferMilesModal.interface';
import * as S from './transferMilesModal.styles';

const Content = ({ open, closeModal, membershipNumber, email, miles }: ContentProps) => {
  const { t } = useTranslation();

  const userTiers = useAppSelector(UserTierState);

  const [step, setStep] = useState<ModalTypes>(ModalTypes.confirmation);
  const [recieptInfo, setRecieptInfo] = useState({
    email: '',
    idNumber: '',
    transfereMile: '',
  });

  const transfer = async () => {
    let data = {};

    if (email) {
      data = {
        mile: miles,
        email: email,
      };
    } else {
      data = {
        mile: miles,
        idNumber: membershipNumber,
      };
    }

    const res = await ajax({
      url: urls.transferMile,
      method: 'POST',
      data,
      showNotif: true,
    });

    if (!res) return;

    setStep(ModalTypes.success);
    setRecieptInfo({
      email: res.email,
      idNumber: res.idNumber,
      transfereMile: res.transfereMile,
    });
  };

  return (
    <S.TransferMilageModal>
      {step === ModalTypes.confirmation && (
        <>
          <S.ModalType1>{t('pages.services.transferMilage.sections.modals.confirmation.title')}</S.ModalType1>

          <S.ModalType2>
            {t('pages.services.transferMilage.sections.modals.confirmation.subtitle', { miles })}
          </S.ModalType2>

          <S.ModalType3>
            {membershipNumber && (
              <S.RowList $remain={false}>
                <S.RowListText>
                  {t('pages.services.transferMilage.sections.modals.confirmation.keyValues.membershipNumber')}
                </S.RowListText>
                <S.RowListText>{membershipNumber}</S.RowListText>
              </S.RowList>
            )}
            {email && (
              <S.RowList $remain={false}>
                <S.RowListText>
                  {t('pages.services.transferMilage.sections.modals.confirmation.keyValues.email')}
                </S.RowListText>
                <S.RowListText>{email}</S.RowListText>
              </S.RowList>
            )}
          </S.ModalType3>

          <S.GrayCard>
            <S.RowList $remain={false}>
              <S.RowListGrayText>
                {t('pages.services.transferMilage.sections.modals.confirmation.keyValues.currentMiles')}
              </S.RowListGrayText>
              <S.RowListGrayText>{userTiers?.rewardMilage}</S.RowListGrayText>
            </S.RowList>
            <S.RowList $remain={false}>
              <S.RowListErrorText>
                {t('pages.services.transferMilage.sections.modals.confirmation.keyValues.miles')}
              </S.RowListErrorText>
              <S.RowListErrorText>{miles}</S.RowListErrorText>
            </S.RowList>
            <S.RowList $remain={true}>
              <S.RowListGrayText>
                {t('pages.services.transferMilage.sections.modals.confirmation.keyValues.remainingMiles')}
              </S.RowListGrayText>
              <S.RowListGrayText>{(userTiers?.rewardMilage || 0) - Number(miles)}</S.RowListGrayText>
            </S.RowList>
          </S.GrayCard>

          <S.BtnContainer>
            <S.BtnActiveText as={Button} styleType="text-button" onClick={closeModal}>
              {t('pages.services.transferMilage.sections.modals.confirmation.cancelCTA')}
            </S.BtnActiveText>
            <S.BtnActiveDefult as={Button} styleType="default" onClick={transfer}>
              {t('pages.services.transferMilage.sections.modals.confirmation.submitCTA')}
            </S.BtnActiveDefult>
          </S.BtnContainer>
        </>
      )}

      {step === ModalTypes.success && (
        <S.TransferMilesReceipt>
          <AlertBox type="success" text={t('pages.services.transferMilage.sections.modals.success.title')} />

          <S.Divider></S.Divider>

          <S.GrayCard>
            <S.RowList $remain={false}>
              <S.RowListText>
                {t('pages.services.transferMilage.sections.modals.success.keyValues.membershipNumber')}
              </S.RowListText>
              <S.RowListText>{recieptInfo.idNumber}</S.RowListText>
            </S.RowList>
            <S.RowList $remain={false}>
              <S.RowListText>
                {t('pages.services.transferMilage.sections.modals.success.keyValues.email')}
              </S.RowListText>
              <S.RowListText>{recieptInfo.email}</S.RowListText>
            </S.RowList>
            <S.RowList $remain={false}>
              <S.RowListText>
                {t('pages.services.transferMilage.sections.modals.success.keyValues.miles')}
              </S.RowListText>
              <S.RowListText>{recieptInfo.transfereMile}</S.RowListText>
            </S.RowList>
          </S.GrayCard>

          <S.BtnContainer>
            <S.BtnActiveDefult as={Button} styleType="default" onClick={closeModal}>
              {t('pages.services.transferMilage.sections.modals.success.closeCTA')}
            </S.BtnActiveDefult>
          </S.BtnContainer>
        </S.TransferMilesReceipt>
      )}
    </S.TransferMilageModal>
  );
};

interface State {
  open: boolean;
}

interface Props {
  ref: any;
  email: string;
  membershipNumber: string;
  miles: string;
}

class TransferMilageModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      open: false,
    };
  }

  openModal = (): void => this.setState({ open: true });

  closeModal = (): void => this.setState({ open: false });

  render() {
    return (
      <BaseModal open={this.state.open} closeModal={this.closeModal}>
        <Content {...this.props} open={this.state.open} closeModal={this.closeModal} />
      </BaseModal>
    );
  }
}

export default TransferMilageModal;
